import { Box, TextField, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { Dispatch, SetStateAction } from "react";
import { BentoSearchFilter, OrganizationProfile } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import { titleCase } from "utils/string";

import QuestionChip from "../../QuestionChip";

const styles = {
  questionBox: {
    border: `1px solid ${grey[300]}`,
    borderRadius: "16px",
    p: 2,
    width: "100%",
  },
};

interface IProps {
  vaSearchFilters: BentoSearchFilter[];
  profile: OrganizationProfile | null;
  setProfile: Dispatch<SetStateAction<OrganizationProfile | null>>;
  includedBentoSearchFilterIds: number[];
  excludedBentoSearchFilterIds: number[];
  setIncludedBentoSearchFilterIds: Dispatch<SetStateAction<number[]>>;
  setExcludedBentoSearchFilterIds: Dispatch<SetStateAction<number[]>>;
  setIsEdited: Dispatch<SetStateAction<boolean>>;
}

export default function OnboardingSettings({
  vaSearchFilters,
  profile,
  setProfile,
  includedBentoSearchFilterIds,
  excludedBentoSearchFilterIds,
  setIncludedBentoSearchFilterIds,
  setExcludedBentoSearchFilterIds,
  setIsEdited,
}: IProps) {
  const includeSearchFilters = vaSearchFilters.filter(
    (f) => f.enableForVaInclude,
  );

  const excludeSearchFilters = vaSearchFilters.filter(
    (f) => f.enableForVaExclude,
  );

  const clickOther = () => {
    setIsEdited(true);
    setProfile((p) => {
      if (!p) return p;
      const copy = { ...p };
      copy.otherType = "";
      return copy;
    });
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ mb: 4 }}>
      <Box sx={styles.questionBox} display="flex" flexDirection="column">
        <Box>
          <Typography gutterBottom>How do you plan to use Bento? *</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="textSecondary" paragraph>
            Select all that apply.
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <QuestionChip
            onClick={() => {
              setIsEdited(true);
              setProfile((p) => {
                if (!p) return p;
                const copy = { ...p };
                if (copy.isUgcCreator) {
                  copy.isUgcCreator = false;
                } else {
                  copy.isUgcCreator = true;
                }
                return copy;
              });
            }}
            isSelected={!!profile?.isUgcCreator}
            label="UGC"
          />
          <QuestionChip
            onClick={() => {
              setIsEdited(true);
              setProfile((p) => {
                if (!p) return p;
                const copy = { ...p };
                if (copy.isInfluencer) {
                  copy.isInfluencer = false;
                } else {
                  copy.isInfluencer = true;
                }
                return copy;
              });
            }}
            isSelected={!!profile?.isInfluencer}
            label="Influencer"
          />
          <Box>
            <QuestionChip
              isSelected={!!profile?.otherType}
              label="Other (please specify):"
              onClick={profile?.otherType ? clickOther : undefined}
            />
            <TextField
              value={profile?.otherType || ""}
              onChange={(e: CustomEvent) => {
                const { value } = e.target;
                setIsEdited(true);
                setProfile((p) => {
                  if (!p) return p;
                  const copy = { ...p };
                  copy.otherType = value;
                  return copy;
                });
              }}
              variant="standard"
              sx={{ ml: 1 }}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={styles.questionBox} display="flex" flexDirection="column">
        <Box>
          <Typography gutterBottom>
            Tell us about you and the content you create
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="textSecondary" paragraph>
            Select all that apply — this helps us tailor opportunities that fit
            you best.
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {includeSearchFilters.map((searchFilter: BentoSearchFilter) => (
            <QuestionChip
              key={searchFilter.id}
              isSelected={
                includedBentoSearchFilterIds.indexOf(searchFilter.id) > -1
              }
              label={searchFilter.vaDisplayName || titleCase(searchFilter.name)}
              onClick={() => {
                setIsEdited(true);
                setIncludedBentoSearchFilterIds((prev) => {
                  if (prev.includes(searchFilter.id)) {
                    return prev.filter((item) => item !== searchFilter.id);
                  } else {
                    return [...prev, searchFilter.id];
                  }
                });
              }}
            />
          ))}
        </Box>
      </Box>
      <Box sx={styles.questionBox} display="flex" flexDirection="column">
        <Box>
          <Typography gutterBottom>
            Are there any categories or industries you'd like us to avoid
            pitching you to?
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="textSecondary" paragraph>
            Select all that apply — we'll make every effort to exclude
            opportunities in these areas.
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {excludeSearchFilters.map((searchFilter: BentoSearchFilter) => (
            <QuestionChip
              key={searchFilter.id}
              color="secondary"
              isSelected={
                excludedBentoSearchFilterIds.indexOf(searchFilter.id) > -1
              }
              label={searchFilter.vaDisplayName || titleCase(searchFilter.name)}
              onClick={() => {
                setIsEdited(true);
                setExcludedBentoSearchFilterIds((prev) => {
                  if (prev.includes(searchFilter.id)) {
                    return prev.filter((item) => item !== searchFilter.id);
                  } else {
                    return [...prev, searchFilter.id];
                  }
                });
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
